import { useCallback, useMemo, useState } from 'react';
import type { StoreId } from '@shared/types.ts';
import { stapleFor } from '@shared/routing.ts';
import { guessPerishable, isPerishable, storesFor } from '@shared/perishable.ts';
import { getStore, useAppState } from '../../data/store.ts';
import { KindToggle, Sheet, SheetHeader, Stepper, money } from '../../components/ui.tsx';
import { Icon } from '../../components/Icon.tsx';
import { navigate } from '../../lib/router.tsx';
import { ProductMatchForm } from './ProductMatchForm.tsx';

export function ItemSheet({ itemId }: { itemId: string }) {
  const store = getStore();
  const list = useAppState((s) => s.list);
  const staples = useAppState((s) => s.staples);
  const products = useAppState((s) => s.products);
  const stores = useAppState((s) => s.stores);
  const item = list.find((i) => i.id === itemId);
  const close = useCallback(() => navigate('/groceries'), []);
  const [editing, setEditing] = useState<StoreId | null>(null);
  const [name, setName] = useState(item?.name ?? '');

  const staple = useMemo(() => (item ? stapleFor(item, staples) : undefined), [item, staples]);
  const perishable = staple ? isPerishable(staple) : guessPerishable(item?.name ?? '');
  // Only stores that take this kind of item.
  const enabled = storesFor(stores.filter((s) => s.enabled), perishable);

  if (!item) {
    return (
      <Sheet title="Item" onClose={close}>
        <SheetHeader title="Item" onClose={close} />
        <div className="empty">This item is no longer on the list.</div>
      </Sheet>
    );
  }

  return (
    <Sheet title={item.name} onClose={close}>
      <SheetHeader title={item.name} subtitle={staple ? 'A staple: remembered for next time' : 'One-off item'} onClose={close} />

      <div className="card stack-lg">
        <div className="field">
          <label htmlFor="item-name">Name</label>
          <input
            id="item-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => name.trim() && name.trim() !== item.name && store.renameItem(item.id, name)}
          />
        </div>
        <div className="row-between">
          <span className="label" style={{ fontSize: 13, fontWeight: 600 }}>
            Quantity
          </span>
          <Stepper value={item.qty} onChange={(n) => store.setQty(item.id, n)} label="quantity" boxed />
        </div>
        <KindToggle
          perishable={perishable}
          guessed={staple?.perishable == null}
          goesTo={enabled.map((s) => s.name)}
          onChange={(p) => store.setItemPerishable(item.id, p)}
        />
        <div className="field">
          <label htmlFor="item-store">Store</label>
          <select
            id="item-store"
            className="select"
            value={item.storeOverride ?? ''}
            onChange={(e) => store.setItemStore([item.id], (e.target.value || undefined) as StoreId | undefined)}
          >
            <option value="">Automatic (cheapest, then fastest)</option>
            {enabled.map((s) => (
              <option key={s.id} value={s.id}>
                Always this time: {s.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <h2 className="section-label">Where we buy it</h2>
      {enabled.map((s) => {
        const p = staple ? products.find((x) => x.stapleId === staple.id && x.storeId === s.id) : undefined;
        if (editing === s.id) {
          return (
            <div className="card" key={s.id}>
              <div className="row-between" style={{ marginBottom: 12 }}>
                <span style={{ fontWeight: 600 }}>{s.name}</span>
              </div>
              <ProductMatchForm
                store={s}
                initial={p}
                defaultTitle={item.name}
                onCancel={() => setEditing(null)}
                onSave={(m) => {
                  store.matchItem(item.id, m);
                  setEditing(null);
                  store.toast(`${item.name} matched at ${s.name}`);
                }}
              />
            </div>
          );
        }
        return (
          <div className="card row" key={s.id} style={{ padding: '12px 12px 12px 16px' }}>
            <div className="grow stack" style={{ gap: 2 }}>
              <span style={{ fontWeight: 600 }}>{s.name}</span>
              {p ? (
                <span className="small muted">
                  {p.title}
                  {p.lastPrice != null ? ` · ${money(p.lastPrice)}` : ''}
                </span>
              ) : (
                <span className="small muted">Not matched</span>
              )}
            </div>
            <button type="button" className="btn" onClick={() => setEditing(s.id)}>
              {p ? 'Edit' : 'Match'}
            </button>
          </div>
        );
      })}

      <button type="button" className="btn btn-danger" onClick={() => (store.removeItem(item.id), close())}>
        <Icon name="trash" size={18} />
        Remove from list
      </button>
    </Sheet>
  );
}
